import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, Linkedin, Facebook, Twitter, Share2, Tag, MessageCircle } from "lucide-react";
import { BLOG_CATEGORIES } from "../lib/content/insights";
import { usePublishedContent } from "../lib/content/published";
import { APP_URLS } from "../lib/content";
import SEO from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-ZA", { day: "2-digit", month: "long", year: "numeric" });

export default function InsightPost() {
  const { slug } = useParams();
  const { posts: blendedPosts } = usePublishedContent();
  const post = blendedPosts.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/insights" replace />;

  // Use the live origin so share buttons work in preview AND in production.
  // Falls back to the configured canonical site URL during SSR / first paint.
  const origin =
    (typeof window !== "undefined" && window.location && window.location.origin) ||
    APP_URLS.site;
  // Canonical URL — used for og:url and as the human-redirect target.
  const url = `${origin}/insights/${post.slug}`;
  // Share URL — routes through the backend so social crawlers get rich OG
  // previews (the SPA returns an empty shell to bots). Humans hitting this
  // URL are 302-redirected straight to `url`.
  const shareUrl = `${origin}/api/share/insights/${post.slug}`;
  const shareTitle = encodeURIComponent(post.title);
  const shareText = encodeURIComponent(`${post.title} — ${post.excerpt}`);
  const linkedin = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  const facebook = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  const twitter = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${shareTitle}`;
  // WhatsApp deep link — works on mobile (opens app) AND desktop (opens web.whatsapp.com).
  const whatsapp = `https://wa.me/?text=${shareText}%20${encodeURIComponent(shareUrl)}`;
  // Native share-sheet fallback (mobile) — also doubles as a copy-link button.
  const handleShare = async (e) => {
    e.preventDefault();
    const shareData = {
      title: post.title,
      text: post.excerpt,
      url: shareUrl,
    };
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch {
      /* user cancelled or unsupported — fall through to clipboard */
    }
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
      }
    } catch {
      /* clipboard unavailable — last-resort prompt */
      // eslint-disable-next-line no-alert
      window.prompt("Copy this link", shareUrl);
    }
  };

  const category = BLOG_CATEGORIES.find((c) => c.slug === post.category)?.label || post.category;

  // Related — same category, exclude self, max 3.
  const related = blendedPosts.filter((p) => p.category === post.category && p.slug !== post.slug).slice(0, 3);

  const breadcrumbItems = [
    ["Home", "/"],
    ["Insights", "/insights"],
    [post.title, `/insights/${post.slug}`],
  ];

  // Article LD-JSON for Google + LinkedIn share previews
  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: `${APP_URLS.site}${post.cover}`,
    datePublished: post.date,
    dateModified: post.date,
    author: { "@type": "Organization", name: post.author },
    publisher: {
      "@type": "Organization",
      name: "HidroVerse",
      logo: { "@type": "ImageObject", url: `${APP_URLS.site}/brand/hidroverse_wordmark_light.png` },
    },
  };

  return (
    <div data-testid={`insight-post-${post.slug}`}>
      <SEO
        title={`Insights — ${post.title}`}
        description={post.excerpt}
        path={`/insights/${post.slug}`}
        image={post.cover}
        type="article"
        ldJson={articleLd}
      />

      {/* HERO */}
      <section
        className="px-6 md:px-12 lg:px-20 pt-28 pb-10"
        style={{ background: "#FAFAF7", color: "#0C2A40" }}
      >
        <div className="max-w-[1000px] mx-auto">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} theme="light" testid="insight-breadcrumb" />
          </div>
          <div
            className="mb-4 text-[0.78rem] font-semibold"
            style={{ color: "rgba(12,42,64,0.55)", letterSpacing: "0.04em" }}
          >
            {category}
          </div>
          <h1
            className="font-display"
            style={{
              fontSize: "clamp(1.9rem, 4.4vw, 3.4rem)",
              lineHeight: 1.1,
              letterSpacing: "-0.015em",
              fontWeight: 500,
              color: "#0C2A40",
            }}
          >
            {post.title}
          </h1>
          <p className="mt-6 text-[1.08rem] font-light text-black/70 leading-relaxed max-w-2xl">
            {post.excerpt}
          </p>
          <div
            className="mt-6 flex flex-wrap items-center gap-5 text-[0.78rem] font-medium"
            style={{ color: "rgba(12,42,64,0.55)" }}
          >
            <span className="inline-flex items-center gap-1.5"><Calendar size={12} aria-hidden /> {fmtDate(post.date)}</span>
            <span className="inline-flex items-center gap-1.5"><Clock size={12} aria-hidden /> {post.readMinutes} min read</span>
            <span>{post.author}</span>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section
        className="px-6 md:px-12 lg:px-20 py-12 md:py-16"
        style={{ background: "#FFFFFF", color: "#0C2A40" }}
        data-testid="insight-body"
      >
        <div className="max-w-[780px] mx-auto">
          <article
            className="insight-prose"
            dangerouslySetInnerHTML={{ __html: post.body }}
          />

          {/* Tags */}
          <div className="mt-12 pt-8 border-t flex flex-wrap items-center gap-2" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
            <Tag size={12} aria-hidden style={{ color: "rgba(12,42,64,0.55)" }} />
            {post.tags.map((t) => (
              <span
                key={t}
                className="px-2.5 py-1 text-[0.7rem] font-medium"
                style={{
                  background: "rgba(12,42,64,0.06)",
                  color: "rgba(12,42,64,0.78)",
                  borderRadius: 3,
                  letterSpacing: "0.04em",
                }}
              >
                {t}
              </span>
            ))}
          </div>

          {/* References */}
          {post.references && post.references.length > 0 && (
            <div className="mt-10" data-testid="insight-references">
              <div
                className="mb-4 text-[0.8rem] font-semibold"
                style={{ color: "rgba(12,42,64,0.6)", letterSpacing: "0.04em" }}
              >
                References
              </div>
              <ol className="list-decimal pl-5 space-y-2 text-[0.86rem] font-light text-black/72 leading-relaxed">
                {post.references.map((r, i) => (<li key={i}>{r}</li>))}
              </ol>
            </div>
          )}

          {/* Social share */}
          <div
            className="mt-10 pt-8 border-t flex flex-wrap items-center justify-between gap-4"
            style={{ borderColor: "rgba(0,0,0,0.08)" }}
            data-testid="insight-share"
          >
            <div
              className="font-mono uppercase"
              style={{ fontSize: "0.66rem", letterSpacing: "0.28em", color: "rgba(12,42,64,0.6)", fontWeight: 700 }}
            >
              Share this insight
            </div>
            <div className="flex items-center gap-2">
              <a
                href={linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 inline-flex items-center justify-center rounded-full transition-transform hover:scale-110"
                style={{ background: "#0A66C2", color: "#FFFFFF" }}
                aria-label="Share on LinkedIn"
                data-testid="share-linkedin"
              >
                <Linkedin size={14} />
              </a>
              <a
                href={whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 inline-flex items-center justify-center rounded-full transition-transform hover:scale-110"
                style={{ background: "#25D366", color: "#FFFFFF" }}
                aria-label="Share on WhatsApp"
                data-testid="share-whatsapp"
              >
                <MessageCircle size={14} />
              </a>
              <a
                href={facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 inline-flex items-center justify-center rounded-full transition-transform hover:scale-110"
                style={{ background: "#1877F2", color: "#FFFFFF" }}
                aria-label="Share on Facebook"
                data-testid="share-facebook"
              >
                <Facebook size={14} />
              </a>
              <a
                href={twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 inline-flex items-center justify-center rounded-full transition-transform hover:scale-110"
                style={{ background: "#000000", color: "#FFFFFF" }}
                aria-label="Share on X"
                data-testid="share-twitter"
              >
                <Twitter size={14} />
              </a>
              <button
                type="button"
                onClick={handleShare}
                className="w-9 h-9 inline-flex items-center justify-center rounded-full transition-transform hover:scale-110"
                style={{ background: "#0C2A40", color: "#FFFFFF" }}
                aria-label="Share or copy link"
                data-testid="share-copy"
                title={typeof navigator !== "undefined" && navigator.share ? "Share…" : "Copy link"}
              >
                <Share2 size={14} />
              </button>
            </div>
          </div>

          <Link
            to="/insights"
            className="inline-flex items-center gap-2 mt-12 font-mono uppercase hover:opacity-100 opacity-80"
            style={{ fontSize: "0.7rem", letterSpacing: "0.22em", color: "#1FB6C1", fontWeight: 700 }}
            data-testid="back-to-insights"
          >
            <ArrowLeft size={13} aria-hidden /> Back to all insights
          </Link>
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section
          className="px-6 md:px-12 lg:px-20 py-12 md:py-16"
          style={{ background: "#FAFAF7" }}
          data-testid="insight-related"
        >
          <div className="max-w-[1300px] mx-auto">
            <div
              className="mb-8 text-[0.84rem] font-semibold"
              style={{ color: "rgba(12,42,64,0.55)" }}
            >
              More on {category.toLowerCase()}
            </div>
            <div className="grid md:grid-cols-3 gap-5 md:gap-6">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/insights/${p.slug}`}
                  className="block group"
                  data-testid={`related-${p.slug}`}
                >
                  <div className="aspect-[16/10] overflow-hidden mb-3" style={{ background: "#F4F5F2", borderRadius: 4 }}>
                    <img
                      src={p.cover}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      onError={(e) => { e.currentTarget.src = "/library/purification/purification_01.webp"; }}
                    />
                  </div>
                  <h3
                    className="font-display group-hover:text-[#1FB6C1] transition-colors"
                    style={{ fontSize: "1rem", lineHeight: 1.3, color: "#0C2A40", fontWeight: 600 }}
                  >
                    {p.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
